import { useEffect, useId, useState } from "react";
import { cortoLimpio, provinciaDeCP } from "@/lib/registro";
import { municipiosDe, municipiosYaCargados } from "@/components/registry/useRegistryRefdata";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "mt-1 bg-white border-[#DCD8D0]";
const avisoCls = "flex flex-wrap items-center gap-2 rounded-lg border border-acento/40 bg-acento/10 px-3 py-2 text-sm text-[#8C3B26]";

// La etiqueta de un campo. Los obligatorios llevan «*» (el formulario explica qué significa) y su campo,
// `aria-required`, que es lo que oye un lector de pantalla.
export function Etiqueta({ htmlFor, obligatorio = false, children }) {
  return (
    <Label htmlFor={htmlFor}>
      {children}
      {obligatorio && <span aria-hidden="true" className="text-[#8C3B26]"> *</span>}
    </Label>
  );
}

// Un selector de país. Su nombre accesible es la etiqueta que lo acompaña (`<Label htmlFor={id}>`), no el país
// elegido: «Nacionalidad», «País del documento»…
export function CountrySelect({ value, onChange, refdata, testid, id, required = false, disabled = false }) {
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className={inputCls} data-testid={testid} aria-required={required || undefined}>
        <SelectValue placeholder="Elige el país" />
      </SelectTrigger>
      <SelectContent>
        {(refdata?.countries || []).map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// Aviso con «Reintentar» cuando las tablas del registro no han llegado (`useRegistryRefdata`): sin ellas, los
// selectores se quedarían vacíos. `listas` nombra las que se echan en falta donde se enseña.
export function AvisoTablas({ refdata, listas = "las listas del formulario (países, tipos de vía, documentos…)" }) {
  if (!refdata?.failed) return null;
  return (
    <div role="alert" className={avisoCls}>
      <span>No se han podido cargar {listas}. Comprueba la conexión y vuelve a intentarlo.</span>
      <Button type="button" size="sm" variant="outline" onClick={refdata.retry}>Reintentar</Button>
    </div>
  );
}

// Dirección del registro de viajeros. En España: código postal → provincia → municipio del INE, que se elige
// de la lista (un CP puede ser de varios municipios). Fuera de España, la localidad escrita y el código postal si lo
// hay (el backend no lo exige; los Mossos lo piden en la entrega). `soloEspana` (la dirección de un establecimiento)
// no deja elegir el país, siempre es España, y no autocompleta con los datos de quien escribe; tampoco con
// `autoCompletar={false}` (la de otra persona, como el segundo conductor).
// `avisarTablas={false}` cuando quien la contiene ya enseña `AvisoTablas` (PersonFields). `obligatorios`: las claves
// que se marcan con «*» (`country`, `street_name`, `postal_code`, `municipality`, que vale también por la localidad).
export default function AddressFields({ value, onChange, refdata, testid = "addr", soloEspana = false, avisarTablas = true,
  autoCompletar = true, obligatorios }) {
  const uid = useId();
  const id = (k) => `${uid}-${k}`;
  const req = (k) => Boolean(obligatorios?.has(k));
  const auto = (token) => (soloEspana || !autoCompletar ? "off" : token);
  const a = soloEspana ? { ...value, country: "ESP" } : value || {};
  const set = (k, v) => onChange({ ...a, [k]: v });
  const espana = a.country === "ESP";
  const prov = espana ? provinciaDeCP(a.postal_code) : null;
  // La lista va con su provincia: al cambiar de provincia no se ofrece la anterior mientras llega la nueva. Cada
  // provincia se pide una vez (`municipiosDe`, en la caché del módulo); la ya cargada se enseña sin esperar.
  const [lista, setLista] = useState(() => {
    const ya = prov ? municipiosYaCargados(prov) : null;
    return ya ? { prov, items: ya, fallo: false } : { prov: null, items: [], fallo: false };
  });
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    if (!prov) return undefined;
    let vivo = true;
    municipiosDe(prov)
      .then((items) => { if (vivo) setLista({ prov, items, fallo: false }); })
      .catch(() => { if (vivo) setLista({ prov, items: [], fallo: true }); });
    return () => { vivo = false; };
  }, [prov, intento]);

  const actual = prov && lista.prov === prov ? lista : { items: [], fallo: false };
  const municipios = actual.items;
  // El municipio elegido se sigue viendo mientras se corrige el CP o llega la lista: va como opción si no está en
  // ella (Radix enseña el texto de la opción elegida; un texto propio en SelectValue choca con su portal).
  const opciones = a.municipality_code && !municipios.some((m) => m.code === a.municipality_code)
    ? [{ code: a.municipality_code, name: a.municipality_name || a.municipality_code }, ...municipios] : municipios;
  const cargando = Boolean(prov) && lista.prov !== prov;
  const reintentar = () => { setLista({ prov: null, items: [], fallo: false }); setIntento((n) => n + 1); };

  const setCP = (cp) => {
    const limpio = cp.toUpperCase().slice(0, 20);
    const nueva = espana ? provinciaDeCP(limpio) : null;
    // El municipio elegido se quita cuando el CP ya es de otra provincia. Con un CP a medias (corrigiendo una
    // cifra) se queda: `faltan` y el backend no dan la dirección por buena hasta que el CP y el municipio cuadran.
    const otra = Boolean(nueva && a.municipality_code && !a.municipality_code.startsWith(nueva));
    onChange({ ...a, postal_code: limpio, ...(otra ? { municipality_code: "", municipality_name: "" } : {}) });
  };

  let ayudaMunicipio = "Elige el municipio";
  if (!a.postal_code) ayudaMunicipio = "Escribe primero el código postal";
  else if (!prov) ayudaMunicipio = "Falta un código postal español";
  else if (cargando) ayudaMunicipio = "Cargando municipios…";
  else if (actual.fallo) ayudaMunicipio = "Lista no disponible";

  return (
    <div className="space-y-3" data-testid={testid}>
      {avisarTablas && <AvisoTablas refdata={refdata} />}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        {!soloEspana && (
          <div className="col-span-2 sm:col-span-3">
            <Etiqueta htmlFor={id("country")} obligatorio={req("country")}>País</Etiqueta>
            <CountrySelect id={id("country")} value={a.country} refdata={refdata} testid={`${testid}-country`} required={req("country")}
              onChange={(v) => onChange({ ...a, country: v, municipality_code: "", municipality_name: "" })} />
          </div>
        )}
        <div className="col-span-2 sm:col-span-3">
          <Label htmlFor={id("street-type")}>Tipo de vía</Label>
          <Select value={a.street_type || "c."} onValueChange={(v) => set("street_type", v)}>
            <SelectTrigger id={id("street-type")} className={inputCls} data-testid={`${testid}-street-type`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {(refdata?.street_types || []).map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 sm:col-span-4">
          <Etiqueta htmlFor={id("street")} obligatorio={req("street_name")}>Nombre de la vía</Etiqueta>
          <Input id={id("street")} className={inputCls} value={a.street_name || ""} autoComplete={auto("address-line1")}
            aria-required={req("street_name") || undefined}
            onChange={(e) => set("street_name", e.target.value)} data-testid={`${testid}-street`} />
        </div>
        <div className="col-span-2 sm:col-span-2">
          <Label htmlFor={id("number")}>Número (vacío si no tiene)</Label>
          {/* Cifras y el guion de «12-14» (que no se junte en «1214»); el registro solo admite cifras y lo avisa. Sin
              `inputMode="numeric"`: el teclado numérico de iOS no tiene guion. */}
          <Input id={id("number")} className={inputCls} value={a.number || ""} autoComplete={auto(undefined)}
            onChange={(e) => set("number", e.target.value.replace(/[^0-9-]/g, "").slice(0, 9))} data-testid={`${testid}-number`} />
        </div>
        {/* Hasta 2 letras o cifras (como el backend): «º» y «ª» se quitan al escribir, y la ayuda lo dice. */}
        {[["staircase", "Escalera"], ["floor", "Piso"], ["door", "Puerta"]].map(([k, label]) => (
          <div key={k} className="sm:col-span-2">
            <Label htmlFor={id(k)}>{label}</Label>
            <Input id={id(k)} className={inputCls} maxLength={2} value={a[k] || ""} autoComplete={auto(undefined)}
              aria-describedby={id("cortos-ayuda")}
              onChange={(e) => set(k, cortoLimpio(e.target.value))} data-testid={`${testid}-${k}`} />
          </div>
        ))}
        <p id={id("cortos-ayuda")} className="col-span-2 sm:col-span-6 text-xs text-[#5C5C5C]">
          Escalera, piso y puerta: hasta 2 letras o cifras, por ejemplo «BJ» (bajo), «IZ» (izquierda) o «DR»
          (derecha); «1º» se escribe «1».
        </p>
        <div className="col-span-2 sm:col-span-2">
          <Etiqueta htmlFor={id("cp")} obligatorio={req("postal_code")}>Código postal</Etiqueta>
          <Input id={id("cp")} className={inputCls} value={a.postal_code || ""} autoComplete={auto("postal-code")}
            aria-required={req("postal_code") || undefined}
            aria-describedby={espana && a.postal_code && !prov ? id("cp-ayuda") : undefined}
            onChange={(e) => setCP(e.target.value)} data-testid={`${testid}-cp`} />
        </div>
        {espana ? (
          <div className="col-span-2 sm:col-span-4">
            <Etiqueta htmlFor={id("municipality")} obligatorio={req("municipality")}>Municipio</Etiqueta>
            <Select value={a.municipality_code || ""} disabled={!prov || municipios.length === 0}
              onValueChange={(v) => {
                const m = municipios.find((x) => x.code === v);
                onChange({ ...a, municipality_code: v, municipality_name: m ? m.name : "" });
              }}>
              <SelectTrigger id={id("municipality")} className={inputCls} data-testid={`${testid}-municipality`}
                aria-required={req("municipality") || undefined}>
                <SelectValue placeholder={ayudaMunicipio} />
              </SelectTrigger>
              <SelectContent>
                {opciones.map((m) => <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="col-span-2 sm:col-span-4">
            <Etiqueta htmlFor={id("locality")} obligatorio={req("municipality")}>Localidad</Etiqueta>
            <Input id={id("locality")} className={inputCls} value={a.municipality_name || ""} autoComplete={auto("address-level2")}
              aria-required={req("municipality") || undefined}
              onChange={(e) => set("municipality_name", e.target.value)} data-testid={`${testid}-locality`} />
          </div>
        )}
      </div>
      {espana && a.postal_code && !prov && (
        <p id={id("cp-ayuda")} className="text-xs text-[#5C5C5C]">
          Un código postal español tiene 5 cifras (de 01000 a 52999); con él se elige el municipio.
        </p>
      )}
      {actual.fallo && (
        <div role="alert" className={avisoCls}>
          <span>No se ha podido cargar la lista de municipios. Comprueba la conexión y vuelve a intentarlo.</span>
          <Button type="button" size="sm" variant="outline" onClick={reintentar}>Reintentar</Button>
        </div>
      )}
    </div>
  );
}
